import { useCallback, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '../../../common/hooks/redux';
import { selectModules } from '../../../common/store/slices/page-config.slice';
import { selectInsightsCaptureTeamId } from '../../../common/store/slices/insights-capture.slice';
import { showConfirm } from '../../../common/store/slices/confirm.slice';
import { INSIGHT_TEAM_SELECTION_ID } from '../constants/constants-insights-capture';
import { useModuleProjectContext } from 'common/contexts/useModuleProjectContext';

export const useInsightsTeams = () => {
  const { activeProject } = useModuleProjectContext();
  const modules = useAppSelector(selectModules);
  const selectedTeamId = useAppSelector(selectInsightsCaptureTeamId);
  const dispatch = useAppDispatch();

  const teams = useMemo(
    () =>
      modules.length > 0
        ? modules[0].projects?.find(
            (project) => project.moduleProjectID === activeProject?.moduleProjectID
          )?.teams
        : [],
    [modules, activeProject]
  );

  const teamName = useCallback(
    (teamId: string) => teams?.find((team) => team.teamID === teamId)?.teamDisplayName ?? '',
    [teams]
  );

  const showTeamSelect = useMemo(() => {
    if (teams && teams.length > 1) {
      return true;
    }
    return false;
  }, [teams]);

  interface ShowTeamDialogProps {
    forceNavigateToNewActivity?: boolean;
  }

  const showTeamDialog = useCallback(
    ({ forceNavigateToNewActivity }: ShowTeamDialogProps) => {
      dispatch(
        showConfirm({
          id: INSIGHT_TEAM_SELECTION_ID,
          param: { forceNavigateToNewActivity },
        })
      );
    },
    [dispatch]
  );

  return useMemo(
    () => ({
      teams: teams ?? [],
      showTeamSelect,
      selectedTeamId,
      showTeamDialog,
      teamName,
    }),
    [teams, showTeamSelect, selectedTeamId, showTeamDialog, teamName]
  );
};
